import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useReducedMotion } from 'framer-motion';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import catechismDataUa from '../../data/catechismData';
import catechismDataRu from '../../data/catechismDataRu';
import { EASE_OUT_EXPO } from '../../components/AnimatedSection';
import './CatechismPage.scss';

const UI_STRINGS = {
    ua: {
        question: 'Питання',
        clickForAnswer: 'Натисніть для відповіді',
        flipBack: 'Повернути',
        part: 'Частина',
        questions: 'питань',
        parts: 'частини',
        allParts: 'Усі частини',
        filterAria: 'Фільтр частин катехізису',
        metaDesc: 'Катехизис для серця і розуму — 52 питання та відповіді про християнську віру',
    },
    ru: {
        question: 'Вопрос',
        clickForAnswer: 'Нажмите для ответа',
        flipBack: 'Вернуть',
        part: 'Часть',
        questions: 'вопросов',
        parts: 'части',
        allParts: 'Все части',
        filterAria: 'Фильтр частей катехизиса',
        metaDesc: 'Катехизис для сердца и ума — 52 вопроса и ответа о христианской вере',
    },
};

const PART_COLORS = {
    1: { bg: '#C97B2A', light: 'rgba(201,123,42,0.08)', accent: '#E8A94A' },
    2: { bg: '#08537c', light: 'rgba(8,83,124,0.08)', accent: '#417ea3' },
    3: { bg: '#2D7D4F', light: 'rgba(45,125,79,0.08)', accent: '#4AA872' },
};

const FlipCard = ({ item, partId, index, strings }) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const prefersReducedMotion = useReducedMotion();
    const colors = PART_COLORS[partId];

    const cardAnim = prefersReducedMotion
        ? {}
        : {
              initial: { opacity: 0, y: 24 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true, amount: 0.1 },
              transition: {
                  duration: 0.7,
                  delay: (index % 3) * 0.1,
                  ease: EASE_OUT_EXPO,
              },
          };

    return (
        <motion.article
            className={`cq-card ${isFlipped ? 'cq-card--flipped' : ''}`}
            onClick={() => setIsFlipped(!isFlipped)}
            role="button"
            tabIndex={0}
            aria-label={`${strings.question} ${item.id}: ${item.question}. ${strings.clickForAnswer}.`}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsFlipped(!isFlipped);
                }
            }}
            {...cardAnim}
        >
            <div className="cq-card__inner">
                {/* ── Front ── */}
                <div className="cq-card__face cq-card__front">
                    <div className="cq-card__top-bar" style={{ backgroundColor: colors.bg }} />
                    <div className="cq-card__body">
                        <div className="cq-card__header">
                            <span className="cq-card__num" style={{ color: colors.bg }}>
                                {item.id}
                            </span>
                            <h3 className="cq-card__question">{item.question}</h3>
                        </div>
                        <div className="cq-card__scripture">
                            <cite className="cq-card__ref">{item.reference}</cite>
                            <p className="cq-card__verse">{item.verse}</p>
                        </div>
                    </div>
                    <div className="cq-card__footer">
                        <span className="cq-card__hint">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 1l4 4-4 4" />
                                <path d="M3 11V9a4 4 0 014-4h14" />
                                <path d="M7 23l-4-4 4-4" />
                                <path d="M21 13v2a4 4 0 01-4 4H3" />
                            </svg>
                            {strings.clickForAnswer}
                        </span>
                    </div>
                </div>

                {/* ── Back ── */}
                <div className="cq-card__face cq-card__back" style={{ '--part-color': colors.bg }}>
                    <div className="cq-card__back-inner">
                        <span className="cq-card__back-num">{item.id}</span>
                        <div className="cq-card__back-divider" />
                        <p className="cq-card__answer">{item.answer}</p>
                    </div>
                    <span className="cq-card__hint cq-card__hint--back">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 1l4 4-4 4" />
                            <path d="M3 11V9a4 4 0 014-4h14" />
                            <path d="M7 23l-4-4 4-4" />
                            <path d="M21 13v2a4 4 0 01-4 4H3" />
                        </svg>
                        {strings.flipBack}
                    </span>
                </div>
            </div>
        </motion.article>
    );
};

const PartHeader = ({ part, strings }) => {
    const prefersReducedMotion = useReducedMotion();
    const colors = PART_COLORS[part.id];

    const anim = prefersReducedMotion
        ? {}
        : {
              initial: { opacity: 0, y: 16 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true, amount: 0.3 },
              transition: { duration: 0.9, ease: EASE_OUT_EXPO },
          };

    return (
        <motion.div className="cq-part-header" {...anim}>
            <div className="cq-part-header__accent" style={{ backgroundColor: colors.bg }} />
            <div className="cq-part-header__content">
                <span className="cq-part-header__label" style={{ color: colors.bg }}>
                    {strings.part} {part.id}
                </span>
                <h2 className="cq-part-header__title">{part.title}</h2>
            </div>
            <div className="cq-part-header__count">
                {part.questions.length} {strings.questions}
            </div>
        </motion.div>
    );
};

const CatechismPage = ({ lang = 'ua' }) => {
    const prefersReducedMotion = useReducedMotion();
    const [activePart, setActivePart] = useState(null);

    const catechismData = lang === 'ru' ? catechismDataRu : catechismDataUa;
    const strings = UI_STRINGS[lang];

    const filteredParts = activePart
        ? catechismData.parts.filter((p) => p.id === activePart)
        : catechismData.parts;

    const totalQuestions = catechismData.parts.reduce(
        (sum, p) => sum + p.questions.length,
        0
    );

    const heroAnim = prefersReducedMotion
        ? {}
        : {
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              transition: { duration: 1.0, ease: EASE_OUT_EXPO },
          };

    const heroTextAnim = (delay) =>
        prefersReducedMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 1.0, delay, ease: EASE_OUT_EXPO },
              };

    return (
        <div className="catechism-page">
            <Helmet>
                <title>Катехизис — Regeneration Church</title>
                <meta
                    name="description"
                    content={strings.metaDesc}
                />
            </Helmet>

            <Header />

            {/* ═══ Hero ═══ */}
            <motion.section className="cq-hero" {...heroAnim}>
                <div className="cq-hero__bg" />
                <div className="cq-hero__content">
                    <motion.div className="cq-hero__badge" {...heroTextAnim(0.2)}>
                        Regeneration Church
                    </motion.div>
                    <motion.h1 className="cq-hero__title" {...heroTextAnim(0.35)}>
                        {catechismData.title}
                    </motion.h1>
                    <motion.p className="cq-hero__subtitle" {...heroTextAnim(0.5)}>
                        {catechismData.subtitle}
                    </motion.p>
                    <motion.div className="cq-hero__stats" {...heroTextAnim(0.65)}>
                        <div className="cq-hero__stat">
                            <span className="cq-hero__stat-num">{totalQuestions}</span>
                            <span className="cq-hero__stat-label">{strings.questions}</span>
                        </div>
                        <div className="cq-hero__stat-divider" />
                        <div className="cq-hero__stat">
                            <span className="cq-hero__stat-num">3</span>
                            <span className="cq-hero__stat-label">{strings.parts}</span>
                        </div>
                    </motion.div>
                </div>
            </motion.section>

            {/* ═══ Sticky Filter ═══ */}
            <nav className="cq-nav" aria-label={strings.filterAria}>
                <div className="cq-nav__inner">
                    <button
                        className={`cq-nav__btn ${activePart === null ? 'cq-nav__btn--active' : ''}`}
                        onClick={() => setActivePart(null)}
                    >
                        {strings.allParts}
                    </button>
                    {catechismData.parts.map((part) => {
                        const isActive = activePart === part.id;
                        const colors = PART_COLORS[part.id];
                        return (
                            <button
                                key={part.id}
                                className={`cq-nav__btn ${isActive ? 'cq-nav__btn--active' : ''}`}
                                style={
                                    isActive
                                        ? { backgroundColor: colors.bg, borderColor: colors.bg, color: '#fff' }
                                        : {}
                                }
                                onClick={() => setActivePart(isActive ? null : part.id)}
                            >
                                <span
                                    className="cq-nav__dot"
                                    style={{ backgroundColor: colors.bg }}
                                />
                                {strings.part} {part.id}
                            </button>
                        );
                    })}
                </div>
            </nav>

            {/* ═══ Cards ═══ */}
            <div className="cq-body">
                {filteredParts.map((part) => (
                    <section key={part.id} className="cq-part" id={`part-${part.id}`}>
                        <PartHeader part={part} strings={strings} />
                        <div className="cq-part__grid">
                            {part.questions.map((q, idx) => (
                                <FlipCard
                                    key={q.id}
                                    item={q}
                                    partId={part.id}
                                    index={idx}
                                    strings={strings}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            <Footer />
        </div>
    );
};

export default CatechismPage;
